/**
 * AppointmentDetail
 * Full appointment detail view with status actions.
 */
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "@jourfx/convex/client";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Clock,
  User,
  Mail,
  Phone,
  CheckCircle2,
  Building2,
  XCircle,
  AlertTriangle,
  Video,
  Copy,
  Pencil,
  Save,
  X,
  MailCheck,
  RotateCcw,
  UserPlus,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Skeleton,
  Separator,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@jourfx/ui";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { ConflictWarnings } from "@/components/scheduling/conflicts/ConflictWarnings";
import { WaitingRoomChatPanel } from "@/components/waitingRoom/WaitingRoomChatPanel";
import { InviteModal } from "@/components/video/InviteModal";
import type { Conflict } from "@/components/scheduling/conflicts/ConflictWarnings";
import { AssessmentPanel } from "./AssessmentPanel";

// Convex Id type for typed API calls
type Id<T extends string> = string & { __tableName: T };

interface AppointmentDetailProps {
  appointmentId: string;
}

const STATUS_I18N_KEYS: Record<string, string> = {
  scheduled: "status.scheduled",
  confirmed: "status.confirmed",
  in_progress: "status.inProgress",
  completed: "status.completed",
  cancelled: "status.cancelled",
  no_show: "status.noShow",
};

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  scheduled: "outline",
  confirmed: "default",
  in_progress: "secondary",
  completed: "secondary",
  cancelled: "destructive",
  no_show: "destructive",
};

export function AppointmentDetail({ appointmentId }: AppointmentDetailProps) {
  const { t } = useTranslation("appointments");
  const { t: tc } = useTranslation("common");
  const appointment = useQuery(api.appointments.index.getDetail, {
    appointmentId: appointmentId as Id<"appointments">,
  }) as any;
  const { user } = useAuth();
  const updateStatus = useMutation(api.appointments.index.updateStatus);
  const updateAppointment = useMutation(api.appointments.index.update);
  const resetAppointment = useMutation(api.appointments.index.resetAppointment);
  const sendReminder = useAction(api.appointments.index.sendReminder);
  const templates = useQuery(api.templates.index.list);
  const gutachterList = useQuery(api.appointments.index.listGutachter);
  const testingMode = useQuery(api.admin.systemSettings.getTestingMode);
  const isTestMode = testingMode?.enabled ?? false;

  const [cancellationReason, setCancellationReason] = useState("");
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showGuestInvite, setShowGuestInvite] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const isGutachter = user?.role === "gutachter";
  const canEdit = user ? ["assistent", "administrator", "super_admin"].includes(user.role) : false;
  const canSendReminder = canEdit;
  const canInviteGuest = user ? ["gutachter", "assistent"].includes(user.role) : false;

  const portalBase = import.meta.env.VITE_CLIENT_APP_URL || "http://localhost:5174";
  const joinUrl = appointment?.accessCode
    ? `${portalBase}?code=${appointment.accessCode}`
    : `${portalBase}?id=${appointmentId}`;

  const [editForm, setEditForm] = useState(() => ({
    notes: "",
    templateId: "",
    gutachterId: "",
    date: "",
    slot: "",
    duration: 30,
  }));

  const editDateOffsetMinutes = useMemo(() => {
    if (!editForm.date) return undefined;
    return parseISO(editForm.date).getTimezoneOffset();
  }, [editForm.date]);

  const editScheduledTimestamp = useMemo(() => {
    if (!editForm.date || !editForm.slot) return undefined;
    const [hour, minute] = editForm.slot.split(":").map(Number);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return undefined;
    const scheduled = new Date(editForm.date);
    scheduled.setHours(hour, minute, 0, 0);
    return scheduled.getTime();
  }, [editForm.date, editForm.slot]);

  const editConflicts = useQuery(
    api.availability.conflicts.checkConflicts,
    editForm.gutachterId && editScheduledTimestamp && editDateOffsetMinutes !== undefined
      ? ({
          gutachterId: editForm.gutachterId as any,
          scheduledAt: editScheduledTimestamp,
          duration: editForm.duration,
          tzOffsetMinutes: editDateOffsetMinutes,
          excludeAppointmentId: appointmentId as Id<"appointments">,
        } as any)
      : "skip"
  ) as Conflict[] | undefined;

  const normalizedEditConflicts = useMemo<Conflict[]>(
    () =>
      (editConflicts ?? []).map((conflict) =>
        conflict.type === "outside_availability"
          ? { ...conflict, severity: "warning" }
          : conflict
      ),
    [editConflicts]
  );

  const hasBlockingConflicts = normalizedEditConflicts.some((c) => c.severity === "error");

  useEffect(() => {
    if (appointment) {
      setNoteDraft(appointment.notes || "");
    }
  }, [appointment?.notes]);

  if (appointment === undefined) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="pt-6 space-y-4">
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-56" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (appointment === null) {
    return (
      <div className="space-y-6">
        <Link
          to="/appointments"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("detail.back")}
        </Link>
        <Card>
          <CardContent className="flex flex-col items-center py-12 text-center">
            <AlertTriangle className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="font-medium">{t("detail.notFound")}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {t("detail.notFoundMessage")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const scheduledDate = new Date(appointment.scheduledAt);
  const scheduledEnd = new Date(appointment.scheduledAt + appointment.duration * 60 * 1000);
  const canConfirm = appointment.status === "scheduled";
  const canCancel = appointment.status === "scheduled" || appointment.status === "confirmed";
  const canNoShow = appointment.status === "confirmed";
  const canJoinVideo =
    appointment.status === "confirmed" || appointment.status === "in_progress" || isTestMode;
  const canReset = ["completed", "cancelled", "no_show"].includes(appointment.status) &&
    appointment.expiresAt > Date.now();

  const handleStatusChange = async (
    status: "confirmed" | "cancelled" | "no_show",
  ) => {
    setIsSubmitting(true);
    try {
      await updateStatus({
        appointmentId: appointmentId as Id<"appointments">,
        status,
        cancellationReason: status === "cancelled" ? cancellationReason || undefined : undefined,
      });
      setShowCancelDialog(false);
      setCancellationReason("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async () => {
    setIsSubmitting(true);
    try {
      await resetAppointment({
        appointmentId: appointmentId as Id<"appointments">,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditing = () => {
    setEditForm({
      notes: appointment.notes || "",
      templateId: appointment.templateId || "",
      gutachterId: appointment.gutachterId || "",
      date: format(new Date(appointment.scheduledAt), "yyyy-MM-dd"),
      slot: format(new Date(appointment.scheduledAt), "HH:mm"),
      duration: appointment.duration || 30,
    });
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!editScheduledTimestamp) return;

    setIsSubmitting(true);
    try {
      await updateAppointment({
        appointmentId: appointmentId as Id<"appointments">,
        patch: {
          notes: editForm.notes || undefined,
          templateId: editForm.templateId || undefined,
          gutachterId: editForm.gutachterId || undefined,
          scheduledAt: editScheduledTimestamp,
          duration: editForm.duration,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
      setIsEditing(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    try {
      await updateAppointment({
        appointmentId: appointmentId as Id<"appointments">,
        patch: {
          notes: noteDraft || undefined,
        },
      });
    } finally {
      setIsSavingNotes(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/appointments"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("detail.back")}
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("detail.title")}</h1>
          <p className="text-muted-foreground">
            {format(scheduledDate, "EEEE, dd. MMMM yyyy", { locale: de })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[appointment.status] ?? "outline"} className="text-sm">
            {STATUS_I18N_KEYS[appointment.status] ? tc(STATUS_I18N_KEYS[appointment.status]) : appointment.status}
          </Badge>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={startEditing}>
              <Pencil className="mr-2 h-4 w-4" /> {tc("actions.edit")}
            </Button>
          )}
        </div>
      </div>

      {isEditing && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("detail.edit.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("detail.edit.template")}</Label>
                <Select
                  value={editForm.templateId}
                  onValueChange={(newTemplateId) => {
                    const tmpl = templates?.find((t: any) => t._id === newTemplateId);
                    setEditForm((f) => ({
                      ...f,
                      templateId: newTemplateId,
                      duration: tmpl?.duration ?? f.duration,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("detail.edit.selectTemplate")} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates?.map((tmpl: any) => (
                      <SelectItem key={tmpl._id} value={tmpl._id as string}>
                        {tmpl.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("detail.edit.examiner")}</Label>
                <Select
                  value={editForm.gutachterId}
                  onValueChange={(value) => setEditForm((f) => ({ ...f, gutachterId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("detail.edit.selectExaminer")} />
                  </SelectTrigger>
                  <SelectContent>
                    {gutachterList?.map((g: any) => (
                      <SelectItem key={g._id} value={g._id as string}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("detail.edit.date")}</Label>
                <Input
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="appointment-edit-time">{t("detail.edit.time")}</Label>
                <Input
                  id="appointment-edit-time"
                  type="time"
                  step="60"
                  value={editForm.slot}
                  onChange={(e) => setEditForm((f) => ({ ...f, slot: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("detail.edit.notesField")}</Label>
              <Input
                value={editForm.notes}
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>

            {normalizedEditConflicts.length > 0 && (
              <ConflictWarnings conflicts={normalizedEditConflicts} />
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleSaveEdit}
                disabled={isSubmitting || !editScheduledTimestamp || hasBlockingConflicts}
              >
                <Save className="mr-2 h-4 w-4" /> {t("detail.edit.save")}
              </Button>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                <X className="mr-2 h-4 w-4" /> {t("detail.edit.cancel")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Appointment info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("detail.appointment")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow
              icon={<Clock className="h-4 w-4" />}
              label={t("detail.dateAndTime")}
              value={`${format(scheduledDate, "dd.MM.yyyy, HH:mm", { locale: de })} – ${format(scheduledEnd, "HH:mm", { locale: de })} Uhr`}
            />
            <InfoRow
              icon={<Clock className="h-4 w-4" />}
              label={t("detail.durationLabel")}
              value={t("detail.durationMinutes", { count: appointment.duration })}
            />
            {appointment.template?.name && (
              <InfoRow
                icon={<CheckCircle2 className="h-4 w-4" />}
                label={t("detail.reason")}
                value={appointment.template.name}
              />
            )}
            {appointment.organization?.name && (
              <InfoRow
                icon={<Building2 className="h-4 w-4" />}
                label={t("detail.businessArea")}
                value={appointment.organization.name}
              />
            )}
          </CardContent>
        </Card>

        {/* Participants */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("detail.participants")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs font-medium uppercase text-muted-foreground mb-2">{t("detail.insuredPerson")}</p>
              {appointment.versicherter ? (
                <div className="space-y-1">
                  <InfoRow icon={<User className="h-4 w-4" />} label="Name" value={appointment.versicherter.name || "—"} />
                  <InfoRow icon={<Mail className="h-4 w-4" />} label="E-Mail" value={appointment.versicherter.email || "—"} />
                  <InfoRow icon={<Phone className="h-4 w-4" />} label="Telefon" value={appointment.versicherter.phone || "—"} />
                  {appointment.versicherter.versichertennummer && (
                    <InfoRow
                      icon={<User className="h-4 w-4" />}
                      label="Versichertennummer"
                      value={appointment.versicherter.versichertennummer}
                    />
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("detail.noVersichertenData")}</p>
              )}
            </div>
            <Separator />
            <div>
              <p className="text-xs font-medium uppercase text-muted-foreground mb-2">{t("detail.examinerLabel")}</p>
              {appointment.gutachter ? (
                <div className="space-y-1">
                  <InfoRow icon={<User className="h-4 w-4" />} label="Name" value={appointment.gutachter.name || "—"} />
                  <InfoRow icon={<Mail className="h-4 w-4" />} label="E-Mail" value={appointment.gutachter.email || "—"} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("detail.noExaminer")}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Access data */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("detail.accessData")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {appointment.accessCode && (
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm text-muted-foreground">{t("detail.accessCode")}</p>
                <p className="font-mono text-lg tracking-widest">{appointment.accessCode}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(appointment.accessCode || "")}>
                <Copy className="mr-2 h-4 w-4" /> {t("detail.copy")}
              </Button>
            </div>
          )}
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground">{t("detail.link")}</p>
              <p className="text-sm truncate">{joinUrl}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(joinUrl)}>
              <Copy className="mr-2 h-4 w-4" /> {t("detail.copy")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("detail.history")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {appointment.history?.length ? (
            appointment.history.map((log: any) => (
              <div key={log._id} className="text-sm text-muted-foreground">
                {format(new Date(log.timestamp), "dd.MM.yyyy HH:mm", { locale: de })} – {log.action}
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">{t("detail.noHistory")}</p>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("detail.edit.notesField")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isGutachter ? (
            <>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder={t("detail.addNotes")}
                className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <div className="flex justify-end">
                <Button onClick={handleSaveNotes} disabled={isSavingNotes}>
                  {isSavingNotes ? <Save className="mr-2 h-4 w-4" /> : null}
                  {t("detail.saveNotes")}
                </Button>
              </div>
            </>
          ) : appointment.notes ? (
            <p className="text-sm">{appointment.notes}</p>
          ) : (
            <p className="text-sm text-muted-foreground">{t("detail.noNotes")}</p>
          )}
        </CardContent>
      </Card>

      <WaitingRoomChatPanel appointmentId={appointmentId} />
      <AssessmentPanel appointmentId={appointmentId} />

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("detail.actionsTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {canJoinVideo && (
              <Button asChild>
                <Link to="/video/$appointmentId" params={{ appointmentId: appointment._id as string }}>
                  <Video className="mr-2 h-4 w-4" />
                  {t("detail.startVideo")}
                </Link>
              </Button>
            )}

            {canSendReminder && (
              <Button
                variant="outline"
                onClick={() => sendReminder({ appointmentId: appointment._id, channel: "email" })}
                disabled={!appointment.versicherter?.email}
              >
                <MailCheck className="mr-2 h-4 w-4" /> {t("detail.sendReminder")}
              </Button>
            )}

            {canInviteGuest && (
              <Button variant="outline" onClick={() => setShowGuestInvite(true)}>
                <UserPlus className="mr-2 h-4 w-4" /> {t("detail.inviteGuest")}
              </Button>
            )}

            {canEdit && canConfirm && (
              <Button variant="outline" onClick={() => handleStatusChange("confirmed")} disabled={isSubmitting}>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {t("detail.confirm")}
              </Button>
            )}

            {canEdit && canNoShow && (
              <Button variant="outline" onClick={() => handleStatusChange("no_show")} disabled={isSubmitting}>
                <AlertTriangle className="mr-2 h-4 w-4" />
                {t("detail.noShow")}
              </Button>
            )}

            {canEdit && canCancel && !showCancelDialog && (
              <Button variant="destructive" onClick={() => setShowCancelDialog(true)} disabled={isSubmitting}>
                <XCircle className="mr-2 h-4 w-4" />
                {t("detail.cancelAppointment")}
              </Button>
            )}

            {canEdit && canReset && (
              <Button variant="outline" onClick={handleReset} disabled={isSubmitting}>
                <RotateCcw className="mr-2 h-4 w-4" />
                {t("detail.resetAppointment")}
              </Button>
            )}
          </div>

          {/* Cancel dialog inline */}
          {canEdit && showCancelDialog && (
            <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-4 space-y-3">
              <p className="text-sm font-medium">{t("detail.cancelDialog.title")}</p>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder={t("detail.cancelDialog.reason")}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                rows={2}
              />
              <div className="flex gap-2">
                <Button variant="destructive" size="sm" onClick={() => handleStatusChange("cancelled")} disabled={isSubmitting}>
                  {t("detail.cancelDialog.confirm")}
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setShowCancelDialog(false); setCancellationReason(""); }}>
                  {t("detail.cancelDialog.cancel")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {showGuestInvite && (
        <InviteModal
          appointmentId={appointmentId}
          onClose={() => setShowGuestInvite(false)}
        />
      )}
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">{icon}</span>
      <span className="text-muted-foreground">{label}:</span>
      <span>{value}</span>
    </div>
  );
}
